import { type RefObject } from 'react';

import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension/selection-extension-plugin-type';
import type {
	SelectionExtensionPluginOptions,
	ExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension/types';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { useEditorToolbarExtensions } from '@confluence/highlight-actions/entry-points/useEditorToolbarExtensions.ts';
import { useJiraWorkCreateSelectionExtension } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtension.ts';
import { useJiraWorkCreateSelectionExtensionV2 } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtensionV2.ts';
import { useNativeTabsSelectionExtension } from '@confluence/native-tabs/entry-points/useNativeTabsSelectionExtension.ts';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import { expVal, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useShouldFetchExternalExtensions } from './useShouldFetchExternalExtensions.ts';
import { useTrelloInboxSelectionExtension } from './useTrelloInboxSelectionExtension.tsx';

export const useSelectionExtensionPluginOptions = ({
	isSidePanelOpenRef,
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
	contentId,
	editorAPIRef,
	flags,
	snippetExtensionList = [],
}: {
	isSidePanelOpenRef: React.MutableRefObject<boolean>;
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
	contentId: string | undefined;
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
	flags?: FlagsStateContainer;
	snippetExtensionList?: ExtensionConfiguration[];
}): SelectionExtensionPluginOptions => {
	const jiraWorkCreateSelectionExtension = useJiraWorkCreateSelectionExtension({
		isSidePanelOpenRef,
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
	});

	const useJiraWorkCreateV2Extension = () =>
		useJiraWorkCreateSelectionExtensionV2({
			rightSidebarContextRef,
			pageModeRef,
			hasEditPermissionRef,
			editorAPIRef,
		});

	// V2 only owns the "Create" section when the bulk work-create experiment is enabled.
	const useConditionalJiraWorkCreateV2Extension = conditionalHooksFactory(
		() => expVal('confluence-bulk-work-create-editor', 'isEnabled', false),
		useJiraWorkCreateV2Extension,
		() => [] as ExtensionConfiguration[],
	);
	const jiraWorkCreateV2Extension = useConditionalJiraWorkCreateV2Extension();
	// During MAUI phase 3, hide Jira work-create only from the block menu while keeping it
	// available in the contextual toolbar.
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- MAUI removal requires both the experiment and phase gate to be enabled.
	const shouldHideJiraWorkCreateBlockMenu =
		isExperimentEnabled('cc-maui-experiment') && fg('cc-maui-phase-3');
	const jiraWorkCreateV2ExtensionList = shouldHideJiraWorkCreateBlockMenu
		? jiraWorkCreateV2Extension.map((extension) => ({
				...extension,
				blockMenu: undefined,
			}))
		: jiraWorkCreateV2Extension;

	const shouldFetchExternalExtensions = useShouldFetchExternalExtensions({
		contentId,
		editorAPIRef,
	});
	const externalExtensions = useEditorToolbarExtensions(contentId, shouldFetchExternalExtensions);

	const useTrelloExtensionWithArgs = () => [
		useTrelloInboxSelectionExtension(contentId, editorAPIRef, flags),
	];
	const useConditionalTrelloExtension = conditionalHooksFactory(
		() => fg('quick_capture_trello_inbox'),
		useTrelloExtensionWithArgs,
		() => [] as ExtensionConfiguration[],
	);
	const trelloExtensionList = useConditionalTrelloExtension();
	const nativeTabsSelectionExtensionList = useNativeTabsSelectionExtension({ editorAPIRef });
	const extensionList = [
		...snippetExtensionList,
		...externalExtensions,
		...jiraWorkCreateV2ExtensionList,
		...trelloExtensionList,
		...nativeTabsSelectionExtensionList,
	];

	// The legacy first-party extension is used only while the bulk work-create experiment is off.
	const extensions: SelectionExtensionPluginOptions['extensions'] = expVal(
		'confluence-bulk-work-create-editor',
		'isEnabled',
		false,
	)
		? {}
		: { firstParty: [jiraWorkCreateSelectionExtension] };

	return {
		extensions,
		extensionList,
	};
};
